import { ApiError, type ApiErrorFallback } from '../types/ApiErrorResponse.types';

import { apiErrorStatus } from './api-response-helper/api-response-helper';

/**
 * Fetchers in `resources/` reject with the `ApiError` the fetch client throws. A caller outside a TanStack
 * query that wants the old "value or fallback" shape says so here, so the swallowing is visible where it
 * happens. Inside a `queryFn` do not use these: the query should see the rejection (error state, retry).
 */

let swallowedErrorObserver: ((error: unknown) => void) | null = null;

/**
 * Registers who learns of a failure the helpers below swallowed, if it is a 5xx or never got an HTTP status
 * (network, abort, JS error). 4xx are expected outcomes (not found, forbidden, validation) and are not reported.
 * Injected like `setRequestObserver`, as this package knows no Sentry SDK.
 */
export function setSwallowedErrorObserver(observer: ((error: unknown) => void) | null) {
    swallowedErrorObserver = observer;
}

/**
 * Whether a failed request is an incident rather than an expected outcome: a 5xx, or a failure that never got an
 * HTTP status (network, JS error), including a `TimeoutError` (the client's own timeout). 4xx are not found,
 * forbidden, validation; an `AbortError` is a superseded request; a failure while the browser is offline is the
 * user's connectivity. None of these is worth a Sentry event.
 */
export function isReportableApiFailure(error: unknown): boolean {
    if (error instanceof Error && error.name === 'AbortError') return false;
    const status = apiErrorStatus(error);
    // A browser that knows it is offline: the user's connectivity, not our incident.
    if (status === undefined && typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    return status === undefined || status >= 500;
}

type FallbackOptions = {
    /** Default true. Pass false where the caller reports the failure itself, so Sentry gets one event, not two. */
    report?: boolean;
};

/**
 * For a catch block that handles a failure itself (toast, error state) and wants a 5xx or network failure in Sentry
 * too. Same path `orNull` and friends use; 4xx and aborts are expected outcomes and are not reported.
 */
export function reportApiFailure(error: unknown): void {
    if (!isReportableApiFailure(error)) return;
    try {
        swallowedErrorObserver?.(error);
    } catch {
        // Observability must never fail the caller.
    }
}

function swallow(error: unknown, options?: FallbackOptions) {
    if (options?.report === false) return;
    reportApiFailure(error);
}

/**
 * The result, or `null` when the API answers one of `statuses` (default: 404, an unknown id is a normal answer).
 * Every other failure rejects, so an outage is never mistaken for "not there". Not reported: the status is an
 * expected outcome.
 */
export async function nullOnStatus<T>(request: Promise<T>, ...statuses: number[]): Promise<T | null> {
    try {
        return await request;
    } catch (error) {
        const status = apiErrorStatus(error);
        if (status !== undefined && (statuses.length > 0 ? statuses : [404]).includes(status)) return null;
        throw error;
    }
}

/** The result, or `null` when the request fails. */
export function orNull<T>(request: Promise<T>, options?: FallbackOptions): Promise<T | null> {
    return request.catch((error: unknown) => {
        swallow(error, options);
        return null;
    });
}

/** The result, or `false` when the request fails. */
export function orFalse<T>(request: Promise<T>, options?: FallbackOptions): Promise<T | false> {
    return request.catch((error: unknown) => {
        swallow(error, options);
        return false as const;
    });
}

/** The result, or the rejection itself as data, for callers that branch on it with `apiResponseHandler`. */
export function orError<T>(request: Promise<T>, options?: FallbackOptions): Promise<T | ApiErrorFallback> {
    return request.catch((error: unknown) => {
        swallow(error, options);
        // A plain copy: a class instance cannot cross the server -> client component boundary, and neither can
        // `headers` (a `Headers`), so it is left out. `data` is parsed JSON, plain all the way down.
        if (error instanceof ApiError) {
            const { isError, status, method, route, requestId, data } = error;
            return { isError, status, method, route, requestId, data } as unknown as ApiErrorFallback;
        }
        return error as ApiErrorFallback;
    });
}
